<script setup lang="ts">
import { ref, nextTick, onMounted, watch } from "vue";
import { cleanCommentHtml } from "@/utils/comment-parser";
import {
  setCaret,
  getCaretPosition,
  removeNode,
  isComplexHtml,
  scrollSelectionIntoView,
} from "@/utils/content-editable";

const props = withDefaults(
  defineProps<{
    readonly: boolean;
    placeholder?: string;
    onlyNumber?: boolean;
    modelValue: string;
    validator?: (content: string) => boolean;
  }>(),
  {
    onlyNumber: false,
    placeholder: "",
  }
);

const emit = defineEmits<{
  (e: "blur"): void;
  (e: "update:modelValue", p: string): void;
  (e: "validityChanged", p: boolean): void;
}>();

const editor = ref<HTMLDivElement>();
const placeholderDiv = ref();
const parentDiv = ref();
const content = ref<string>("");
const isValid = ref<boolean>(true);
const clear = (keepFocus = false) => {
  if (!editor.value) return;
  editor.value.innerHTML = "";
  if (keepFocus) {
    nextTick(() => focusAndCaretToEnd());
  }
};
const focusAndCaretToEnd = () => {
  if (!editor.value) return;
  setCaret(editor.value.childNodes[editor.value.childNodes.length - 1]);
  setTimeout(() => editor.value?.focus(), 0);
};

const focus = () => {
  if (!editor.value) return;
  editor.value.focus();
  setCaret(
    currentIndex && editor.value.childNodes[currentIndex]
      ? editor.value.childNodes[currentIndex]
      : editor.value.childNodes[editor.value.childNodes.length - 1]
  );
};
onMounted(() => {
  if (!editor.value) return;
  editor.value.addEventListener("paste", function (e: ClipboardEvent) {
    e.preventDefault();
    const { clipboardData } = e;
    if (!clipboardData) return;
    const text = clipboardData.getData("text/plain");
    addComplexHtml(text);
  });

  content.value = cleanCommentHtml(props.modelValue);
  isValid.value = validate(content.value);
  setInnerHTML(content.value);
  setTimeout(() => setEditorHeight(), 5);
});

function validate(content: string): boolean {
  if (!props.validator) return true;
  return props.validator(content);
}

watch(isValid, (newVal) => {
  emit("validityChanged", newVal);
});

let currentIndex: any;
const onInput = () => {
  if (!editor.value) return;
  const { index } = getCaretPosition(editor.value.childNodes);
  currentIndex = index;
  const value = editor.value.innerHTML;
  isValid.value = validate(value);
  if (!isValid.value) {
    return;
  }
  content.value = `${value}`;
  emit("update:modelValue", value.replace("&nbsp;", " "));
  scrollSelectionIntoView(editor.value);
};

const onKeyDown = (ev: KeyboardEvent) => {
  if (!editor.value) return;
  const selection = document.getSelection();
  const node = selection?.anchorNode;

  if (props.onlyNumber) {
    if (editor.value.innerHTML.indexOf(".") > -1 && ev.key == ".") {
      ev.preventDefault();
      return;
    }

    const reg = /[1-9.]/;
    if (!reg.test(ev.key) && ev.key != "Backspace") {
      ev.preventDefault();
      return;
    }
  }
  if (!isComplexHtml(node, editor.value)) return;
  if (ev.key === "Backspace") {
    removeNode(node?.parentNode);
    emit("update:modelValue", editor.value.innerHTML);
  }
};

const setInnerHTML = (value: string) => {
  if (!editor.value) return;
  editor.value.innerHTML = value.trim();
  scrollSelectionIntoView(editor.value);
};

const addComplexHtml = (value: string) => {
  setInnerHTML(value);
  setTimeout(() => editor.value?.focus(), 0);
  nextTick(() => {
    if (!editor.value) return;
    setCaret(
      currentIndex && editor.value.childNodes[currentIndex]
        ? editor.value.childNodes[currentIndex]
        : editor.value.childNodes[editor.value.childNodes.length - 1]
    );
    emit("update:modelValue", editor.value.innerHTML);
    scrollSelectionIntoView(editor.value);
  });
};

const setEditorHeight = () => {
  if (!editor.value) return;
  if (props.modelValue?.length) {
    editor.value.style.minHeight = "auto";
  } else {
    if (!placeholderDiv.value) return;
    const parentStyle = getComputedStyle(parentDiv.value);
    placeholderDiv.value.style.paddingTop = parentStyle.paddingTop;
    placeholderDiv.value.style.paddingLeft = parentStyle.paddingLeft;
    placeholderDiv.value.style.paddingBottom = parentStyle.paddingBottom;
    placeholderDiv.value.style.paddingRight = parentStyle.paddingRight;
    const placeholderRect = placeholderDiv.value.getBoundingClientRect();
    const paddingVertical =
      parseInt(parentStyle.paddingTop) + parseInt(parentStyle.paddingBottom);
    editor.value.style.minHeight =
      placeholderRect.height - paddingVertical + "px";
  }
};

watch(
  () => props.modelValue,
  () => {
    nextTick(() => setEditorHeight());
  }
);

defineExpose({
  clear,
  focus,
  addComplexHtml,
});
</script>
<template>
  <div class="text-editor" ref="parentDiv">
    <div
      ref="placeholderDiv"
      class="text-editor-placeholder"
      v-if="!modelValue || !modelValue.length"
    >
      {{ placeholder }}
    </div>
    <div
      class="text-editor-content"
      :class="{ invalid: !isValid }"
      ref="editor"
      @blur="$emit('blur')"
      @input="onInput"
      @keydown="onKeyDown"
      :contenteditable="!readonly"
    />
  </div>
</template>

<style scoped>
.text-editor {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  --font-size: var(--text-editor-font-size, var(--font-size-m));
  --line-height: calc(var(--font-size) * 1.5);
  margin: var(--text-editor-margin, var(--ion-padding)) 0;
}

.text-editor-content {
  background-color: inherit;
  color: inherit;
  display: inline-block;
  width: auto;
  min-height: var(--line-height);
  line-height: var(--line-height);
  border-width: 1px;
  border-color: transparent;
  border-style: solid;

  font-size: var(--font-size);
  width: 100%;

  outline: 0;

  -moz-user-select: text;
  -khtml-user-select: text;
  -webkit-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

.text-editor-content.invalid {
  border-color: var(--ion-color-danger);
}

.text-editor-placeholder {
  position: absolute;
  pointer-events: none;
  padding: 0;
  font-size: var(--font-size);
  min-height: var(--line-height);
  line-height: var(--line-height);
  top: 0;
  left: 0;
  right: 0;
  color: var(--f-color-disabled);
}
</style>
